import { useQuery, useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { QueryModule } from "@/data/queryModules";
import { MutationEffectsOptions, useMutationEffects } from "@/data/useMutationEffects";
import { AppQueryOptions, AppInfiniteQueryOptions, AppMutationOptions } from "@povio/openapi-codegen-cli";
import { NotificationsModels } from "./notifications.models";
import { NotificationsApi } from "./notifications.api";

export namespace NotificationsQueries {

export const moduleName = QueryModule.Notifications;

export const keys = {
    all: [moduleName] as const,
    getUser: (limit?: number, order?: string, filter?: NotificationsModels.NotificationFilterDto, page?: number, cursor?: string, ) => [...keys.all, "/notifications", limit, order, filter, page, cursor] as const,
    getUserInfinite: (limit?: number, order?: string, filter?: NotificationsModels.NotificationFilterDto, cursor?: string, ) => [...keys.all, "/notifications", "infinite", limit, order, filter, cursor] as const,
    getUnread: () => [...keys.all, "/notifications/unread", ] as const,
};


 /** 
 * Query `useGetUser`
 * @summary Get user notifications
 * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
 * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): createdAt, readAt, type, updatedAt. Example: `createdAt`
 * @param { NotificationsModels.NotificationFilterDto } object.filter Query parameter
 * @param { number } object.page Query parameter. 1-indexed page number to begin from
 * @param { string } object.cursor Query parameter. ID of item to start after
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<NotificationsModels.PaginatedNotificationResponseDto> } User notifications retrieved successfully
 * @statusCodes [200, 401]
 */
export const useGetUser = <TData>({ limit, order, filter, page, cursor }: { limit: number, order?: string, filter?: NotificationsModels.NotificationFilterDto, page?: number, cursor?: string,  }, options?: AppQueryOptions<typeof NotificationsApi.getUser, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getUser(limit, order, filter, page, cursor),
    queryFn: () =>  
    
    NotificationsApi.getUser(limit, order, filter, page, cursor)
    ,
    ...options,
  });
};



/** 
 * Infinite query `useGetUserInfinite
 * @summary Get user notifications
 * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
 * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): createdAt, readAt, type, updatedAt. Example: `createdAt`
 * @param { NotificationsModels.NotificationFilterDto } object.filter Query parameter
 * @param { number } object.page Query parameter. 1-indexed page number to begin from
 * @param { string } object.cursor Query parameter. ID of item to start after
 * @param { AppInfiniteQueryOptions } options Infinite query options
 * @returns { UseInfiniteQueryResult<NotificationsModels.PaginatedNotificationResponseDto> } User notifications retrieved successfully
 * @statusCodes [200, 401]
 */
export const useGetUserInfinite = <TData>({ limit, order, filter, cursor }: { limit: number, order?: string, filter?: NotificationsModels.NotificationFilterDto, cursor?: string,  }, options?: AppInfiniteQueryOptions<typeof NotificationsApi.getUser, TData>) => {
  

  return useInfiniteQuery({
    queryKey: keys.getUserInfinite(limit, order, filter, cursor),
    queryFn: ({ pageParam }) =>  
    
    NotificationsApi.getUser(limit, order, filter, pageParam, cursor)
    ,
    initialPageParam: 1,
    getNextPageParam: ({ page, totalItems, limit: limitParam }) => {
      const pageParam = page ?? 1;
      return pageParam * limitParam < totalItems ? pageParam + 1 : null;
    },
    ...options,
  });
};




 /** 
 * Mutation `useDeleteAll`
 * @summary Delete all notifications for the authenticated user
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<void> } 
 * @statusCodes [204, 401]
 */
export const useDeleteAll = (options?: AppMutationOptions<typeof NotificationsApi.deleteAll, {  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: () => 
      
            NotificationsApi.deleteAll()
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};





 /** 
 * Query `useGetUnread`
 * @summary Get unread notifications
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<NotificationsModels.GetUnreadResponse> } 
 * @statusCodes [200, 401]
 */
export const useGetUnread = <TData>(options?: AppQueryOptions<typeof NotificationsApi.getUnread, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getUnread(),
    queryFn: NotificationsApi.getUnread
    ,
    ...options,
  });
};








 /** 
 * Mutation `useMarkAsRead`
 * @summary Mark notification as read
 * @param { string } mutation.id Path parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<NotificationsModels.NotificationDto> } 
 * @statusCodes [200, 401]
 */
export const useMarkAsRead = (options?: AppMutationOptions<typeof NotificationsApi.markAsRead, { id: string,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { id } ) => 
      
            NotificationsApi.markAsRead(id)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useMarkAllAsRead`
 * @summary Mark all notifications as read
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<void> } 
 * @statusCodes [204, 401]
 */
export const useMarkAllAsRead = (options?: AppMutationOptions<typeof NotificationsApi.markAllAsRead, {  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: () => 
      
            NotificationsApi.markAllAsRead()
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useDeleteNotification`
 * @summary Delete notification
 * @param { string } mutation.id Path parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<void> } 
 * @statusCodes [204, 401]
 */
export const useDeleteNotification = (options?: AppMutationOptions<typeof NotificationsApi.deleteNotification, { id: string,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { id } ) => 
      
            NotificationsApi.deleteNotification(id)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};





}
